import { BaseResource } from './base';
import { Attendance, ListResponse, PaginationParams } from '../types';

export interface CreateAttendanceData {
    studentId: string;
    courseId: string;
    date: string;
    status: 'present' | 'absent' | 'tardy' | 'excused';
    notes?: string;
    recordedBy: string;
}

export interface UpdateAttendanceData
    extends Partial<Omit<CreateAttendanceData, 'studentId' | 'courseId' | 'date'>> { }

export interface AttendanceFilters extends PaginationParams {
    studentId?: string;
    courseId?: string;
    status?: 'present' | 'absent' | 'tardy' | 'excused';
    startDate?: string;
    endDate?: string;
    recordedBy?: string;
}

export class AttendanceResource extends BaseResource {
    /**
     * List all attendance records with optional filtering and pagination
     */
    async list(filters?: AttendanceFilters): Promise<ListResponse<Attendance>> {
        return super.requestGet<ListResponse<Attendance>>('/attendance', filters);
    }

    /**
     * Get a specific attendance record by ID
     */
    async getAttendance(attendanceId: string): Promise<Attendance> {
        return super.requestGet<Attendance>(`/attendance/${attendanceId}`);
    }

    /**
     * Create a new attendance record
     */
    async createAttendance(data: CreateAttendanceData): Promise<Attendance> {
        return super.requestPost<Attendance>('/attendance', data);
    }

    /**
     * Update an existing attendance record
     */
    async updateAttendance(attendanceId: string, data: UpdateAttendanceData): Promise<Attendance> {
        return super.requestPatch<Attendance>(`/attendance/${attendanceId}`, data);
    }

    /**
     * Delete an attendance record
     */
    async deleteAttendance(attendanceId: string): Promise<{ success: boolean; message: string }> {
        return super.requestDelete<{ success: boolean; message: string }>(`/attendance/${attendanceId}`);
    }

    /**
     * Bulk create attendance records
     */
    async bulkCreate(records: CreateAttendanceData[]): Promise<{ created: Attendance[]; errors: any[] }> {
        return super.requestPost<{ created: Attendance[]; errors: any[] }>('/attendance/bulk', { records });
    }

    /**
     * Get attendance statistics for a student
     */
    async getStudentStats(
        studentId: string,
        filters?: { courseId?: string; startDate?: string; endDate?: string }
    ): Promise<any> {
        return super.requestGet<any>(`/attendance/student/${studentId}/stats`, filters);
    }

    /**
     * Get attendance statistics for a course
     */
    async getCourseStats(
        courseId: string,
        filters?: { date?: string; startDate?: string; endDate?: string }
    ): Promise<any> {
        return super.requestGet<any>(`/attendance/course/${courseId}/stats`, filters);
    }

    /**
     * Mark attendance for multiple students
     */
    async markBulkAttendance(
        courseId: string,
        date: string,
        records: Array<{ studentId: string; status: 'present' | 'absent' | 'tardy' | 'excused'; notes?: string }>
    ): Promise<{ created: Attendance[]; errors: any[] }> {
        return super.requestPost<{ created: Attendance[]; errors: any[] }>(
            `/attendance/course/${courseId}/bulk`,
            { date, records }
        );
    }
}
